import { uniqueId } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function useButtonSplit({ onChange, options = [], selected }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const chevronRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(selected || 0);

  // Ids únicos por instancia para el cableado ARIA (aria-controls,
  // aria-labelledby). Con `disablePortal`, varios ButtonSplit en la misma página
  // producirían ids duplicados y asociaciones de lector incorrectas si fueran
  // literales. `uniqueId` (lodash, contador determinista) es el patrón del repo.
  const [instanceId] = useState(() => uniqueId('button-split-'));
  const chevronId = `${instanceId}-chevron`;
  const menuId = `${instanceId}-menu`;

  const focusChevron = () => {
    chevronRef.current?.focus();
  };

  // Factoría: devuelve el handler ligado al índice para evitar arrows con lógica
  // en el JSX. El evento del click no se usa.
  const handleMenuItemClick = (index) => () => {
    setSelectedIndex(index);
    onChange(index);
    setOpen(false);
    focusChevron();
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // Cierre por click fuera: NO se devuelve el foco al chevron. El foco debe
  // seguir al elemento que el usuario ha pulsado (patrón WAI-ARIA menu button:
  // el foco vuelve al trigger solo con Escape/Tab o al seleccionar una opción).
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  // Tab cierra el menú (MUI no lo cierra solo con Tab). Coloca el foco en el
  // chevron pero NO hace preventDefault: así el Tab por defecto del navegador
  // continúa desde el trigger al siguiente/anterior elemento tabulable, como
  // espera el patrón WAI-ARIA menu button (Tab avanza, Shift+Tab retrocede).
  // Escape se gestiona en el listener global de abajo.
  const handleListKeyDown = (event) => {
    if (event.key === 'Tab') {
      setOpen(false);
      focusChevron();
    }
  };

  // Claves estables para el listado del menú sin usar el índice del array como
  // key (regla S6479). Se deriva de `id`/`text`; para ids repetidos o varios
  // separadores (que comparten `id: 'separator'`) se añade un contador de
  // ocurrencia, garantizando unicidad sin depender de la posición del array.
  const optionsWithKeys = useMemo(() => {
    const occurrences = new Map();
    return options.map((option, index) => {
      const base = option.id || option.text || 'option';
      const count = occurrences.get(base) ?? 0;
      occurrences.set(base, count + 1);
      return { option, index, key: count === 0 ? base : `${base}-${count}` };
    });
  }, [options]);

  useEffect(() => {
    if (selected) {
      setSelectedIndex(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        // Evita que un contenedor externo que también cierre con Escape a nivel
        // de documento (p. ej. un modal) reciba el mismo evento: un solo Escape
        // solo debe cerrar el menú del ButtonSplit.
        event.stopPropagation();
        setOpen(false);
        focusChevron();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  return { anchorRef, chevronRef, chevronId, menuId, open, optionsWithKeys, selectedIndex, handleMenuItemClick, handleToggle, handleClose, handleListKeyDown };
}
